import { Button } from '@merels/ui'
import { useEffect, useState } from 'react'

interface RoomErrorViewProps {
  error: string
  isClosed?: boolean
  opensAt?: string | null
  onBackToLobby: () => void
  onOpeningTimeReached?: () => void
}

const COUNTDOWN_THRESHOLD_MS = 10 * 60 * 1000
const OPENING_RETRY_DELAY_MS = 2 * 1000

function formatCountdown(remainingMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

export function RoomErrorView({ error, isClosed = false, opensAt = null, onBackToLobby, onOpeningTimeReached }: RoomErrorViewProps) {
  const [now, setNow] = useState(() => Date.now())
  const openingTime = opensAt ? Date.parse(opensAt) : Number.NaN
  const remainingMs = openingTime - now

  useEffect(() => {
    if (!isClosed || !Number.isFinite(openingTime)) return
    const interval = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(interval)
  }, [isClosed, openingTime])

  useEffect(() => {
    if (!isClosed || !Number.isFinite(openingTime) || !onOpeningTimeReached) return
    const retryDelay = Math.max(openingTime - Date.now(), OPENING_RETRY_DELAY_MS)
    const timeout = window.setTimeout(onOpeningTimeReached, retryDelay)
    return () => window.clearTimeout(timeout)
  }, [isClosed, openingTime, onOpeningTimeReached])

  let message = error
  let formattedOpeningTime: string | null = null
  let countdown: string | null = null
  if (isClosed) {
    if (!opensAt) {
      message = 'This room is closed until the owner opens it.'
    } else {
      formattedOpeningTime = new Intl.DateTimeFormat(undefined, { dateStyle: 'full', timeStyle: 'short' }).format(new Date(opensAt))
      message = 'This room is currently closed. It will open '
      if (remainingMs <= COUNTDOWN_THRESHOLD_MS && remainingMs > 0) {
        countdown = formatCountdown(remainingMs)
      }
    }
  }

  return (
    <div className="max-w-md w-full text-center mx-auto my-12 p-8 bg-surface-hover/30 border border-muted/20 rounded-2xl shadow-xl backdrop-blur-md flex flex-col items-center">
      <div className="w-full mb-6 overflow-hidden rounded-xl shadow-md border border-muted/10">
        <img
          src={isClosed ? '/room-closed.jpg' : '/room-not-found.jpg'}
          alt=""
          className="w-full h-48 object-cover"
        />
      </div>
      <p className="text-base text-primary font-medium mb-8 bg-surface/50 border border-muted/10 p-4 rounded-xl">
        <span>
          {message}
          {formattedOpeningTime && <time dateTime={opensAt || undefined}>{formattedOpeningTime}</time>}
          {formattedOpeningTime && '.'}
        </span>
        {countdown && (
          <span data-testid="room-opening-countdown" className="mt-3 block text-4xl font-bold tabular-nums text-primary">
            {countdown}
          </span>
        )}
      </p>
      <Button onClick={onBackToLobby} size="lg">
        Back to Lobby
      </Button>
    </div>
  )
}
